/**
 * Projectile simulation: spawning, integration, and collision against hulls and water.
 *
 * Everything here is pooled and deterministic. Rounds are integrated semi-implicitly at
 * the fixed 1/60 s step with quadratic drag, which is what produces the visible arc the
 * whole weapon fantasy rests on — a shell at 4 km is in the air for over five seconds and
 * falls most of a hundred metres while it travels.
 *
 * Collision is a swept test, not a point test. At 1,000 m/s a round covers 16.7 m per
 * tick, so a naive per-tick position check would shoot straight through a 9 m skiff most
 * of the time.
 */

import type { Projectile, ProjectileKind, Vessel, EntityId } from './types';
import type { WaveField } from './waves';
import type { Rng } from './rng';
import { waterHeightAt } from './waves';
import { GRAVITY, closestApproach, type Vec3 } from './math';
import { AUTOCANNON, TORPEDO, DEPTH_CHARGE, TORPEDO_LOCK } from '../content/weapons';
import { vesselSpec } from '../content/vessels';
import { tentaclePose, tentacleCentrePoint, type TentaclePose } from '../content/tentaclePose';

/** Pool size. Plan D1 budgets 512 shells with ~150 typically active. */
const POOL_SIZE = 640;

export interface ProjectileHit {
  projectile: Projectile;
  /** Non-null when a hull was struck. */
  vessel: Vessel | null;
  point: Vec3;
  /** Impact speed, for splash and sound scaling. */
  speed: number;
  ricochet: boolean;
  /** True when the strike was on the Kraken's staring eye rather than its hull. */
  eye?: boolean;
}

export class ProjectilePool {
  readonly items: Projectile[] = [];
  private nextId = 1;

  constructor() {
    for (let i = 0; i < POOL_SIZE; i++) {
      this.items.push({
        id: 0,
        kind: 'shell',
        pos: { x: 0, y: 0, z: 0 },
        vel: { x: 0, y: 0, z: 0 },
        age: 0,
        phase: 'spent',
        hostile: false,
        tracer: false,
        damage: 0,
        ownerId: null,
        targetId: null,
        rigged: null,
        active: false,
        skips: 0,
        originX: 0,
        originZ: 0,
      });
    }
  }

  /** Live projectiles only. Allocation-free for the common empty case. */
  get active(): Projectile[] {
    return this.items.filter((p) => p.active);
  }

  get activeCount(): number {
    let n = 0;
    for (const p of this.items) if (p.active) n++;
    return n;
  }

  /**
   * Claim a free slot. Returns null when the pool is exhausted rather than growing it —
   * an unbounded pool would hide a leak, and the budget is a design constraint.
   */
  spawn(
    kind: ProjectileKind,
    pos: Vec3,
    vel: Vec3,
    opts: {
      hostile: boolean;
      damage: number;
      tracer?: boolean;
      ownerId?: EntityId | null;
      targetId?: EntityId | null;
      rigged?: 'hit' | 'miss' | null;
      fuseDepth?: number;
    },
  ): Projectile | null {
    for (const p of this.items) {
      if (p.active) continue;
      p.id = this.nextId++;
      p.kind = kind;
      p.pos.x = pos.x; p.pos.y = pos.y; p.pos.z = pos.z;
      p.vel.x = vel.x; p.vel.y = vel.y; p.vel.z = vel.z;
      p.age = 0;
      p.phase = 'air';
      p.hostile = opts.hostile;
      p.tracer = opts.tracer ?? false;
      p.damage = opts.damage;
      p.ownerId = opts.ownerId ?? null;
      p.targetId = opts.targetId ?? null;
      p.rigged = opts.rigged ?? null;
      p.skips = 0;
      p.originX = pos.x;
      p.originZ = pos.z;
      if (opts.fuseDepth !== undefined) p.fuseDepth = opts.fuseDepth;
      p.active = true;
      return p;
    }
    return null;
  }

  release(p: Projectile): void {
    p.active = false;
    p.phase = 'spent';
  }

  clear(): void {
    for (const p of this.items) {
      p.active = false;
      p.phase = 'spent';
    }
  }
}

/**
 * Advance every projectile one tick and report what it hit.
 *
 * `vessels` must be the live list; dead hulls are skipped so a sinking wreck stops
 * absorbing rounds meant for the boat behind it.
 */
export function stepProjectiles(
  pool: ProjectilePool,
  vessels: readonly Vessel[],
  waves: WaveField,
  time: number,
  dt: number,
  rng: Rng,
  /** High-speed screws, as a multiplier on the torpedo's whole speed curve. */
  torpedoSpeedScale = 1,
): ProjectileHit[] {
  const hits: ProjectileHit[] = [];
  // One pose per limb per call: every projectile this tick collides against the same
  // instant of the animation, which is also the instant the renderer draws.
  const poses = new Map<number, TentaclePose>();

  for (const p of pool.items) {
    if (!p.active) continue;

    p.age += dt;
    const from: Vec3 = { x: p.pos.x, y: p.pos.y, z: p.pos.z };

    if (p.kind === 'shell' || p.kind === 'rpg' || p.kind === 'rocket') {
      integrateBallistic(p, dt);
    } else if (p.kind === 'torpedo') {
      integrateTorpedo(p, vessels, dt, torpedoSpeedScale);
    } else if (p.kind === 'depthCharge') {
      integrateDepthCharge(p, dt);
    }

    // --- Hull collision, swept over this tick's segment -------------------------------
    const step: Vec3 = { x: p.pos.x - from.x, y: p.pos.y - from.y, z: p.pos.z - from.z };
    let struck: Vessel | null = null;
    let strikeT = Infinity;
    let struckEye = false;

    for (const v of vessels) {
      if (v.state === 'dead' || v.state === 'sinking') continue;
      if (v.id === p.ownerId) continue;
      // Hostile rounds only hurt friendlies and the rig; player rounds only hurt hostiles
      // — except that friendly fire is real, so a player round CAN hit a friendly hull.
      if (p.hostile && v.faction === 'hostile') continue;
      if (v.state === 'departing') continue; // safe vessels are invulnerable
      /*
       * A submerged Kraken is not a hull anything can strike.
       *
       * The plan is explicit that the dread phase offers "no shootable target,
       * deliberately", and the creature stays under through the tentacle phase too — the
       * limbs are the fight, the body is not there yet. That was already the intent; it
       * only became load-bearing when the spec grew from a 120 m "combat core" to the
       * animal's real kilometre. An invisible thousand-metre volume sitting between the
       * platform and its targets silently ate every round aimed at a limb, and the finale
       * became unwinnable — measured, not guessed: the rig fell from 100 to 0 during the
       * tentacle phase with the boss on exactly full health and not one hit registered.
       *
       * Depth charges reach it by their own path (`registerChargeNear`), which is the
       * authored answer to a creature that is down.
       */
      if (v.kind === 'kraken' && v.submerged) continue;

      /*
       * The staring eye, tested before the mantle it sits on.
       *
       * During the finale's stare the eye is the one shot that matters, and it is a
       * feature ON the huge body capsule — an eye-aimed round would otherwise register
       * as a generic mantle plink at whatever point the capsule test found first. The
       * dedicated sphere wins any tie by being checked first at equal-or-closer t.
       */
      if (v.eyeTarget) {
        const ca = closestApproach(from, step, v.eyeTarget, 1);
        if (ca.dist <= v.eyeTarget.r && ca.t <= strikeT) {
          strikeT = ca.t;
          struck = v;
          struckEye = true;
        }
      }

      const spec = vesselSpec(v.kind);

      if (spec.hitSpheres) {
        /*
         * A body with an authored shape is tested against that shape — and against the
         * shape as it IS this tick, not as it was authored.
         *
         * The centreline capsule below is right for anything that floats and badly wrong
         * for a limb: it put the entire hit volume in a horizontal sausage a few metres
         * above the waterline, so rounds into the visible column above or below that band
         * passed straight through. Adam found it the way it presents — "they only take
         * damage when you shoot them in one particular spot".
         *
         * `content/tentaclePose.ts` is the shared law: emergence, the travelling wave AND
         * the grip wrap, identical to what the renderer bends the mesh with. A limb
         * coiled over a hull or hooked onto the deck edge is shootable exactly where it
         * visibly is. Only projectiles that survive the broad phase pay for it, and the
         * pose is resolved once per limb per call.
         */
        const bound = spec.hitBoundRadius ?? 0;
        const broad = closestApproach(from, step, v.pos, 1);
        if (broad.dist > bound) continue;

        let pose = poses.get(v.id);
        if (!pose) {
          pose = tentaclePose(v, vessels, time);
          poses.set(v.id, pose);
        }
        const c = Math.cos(v.heading);
        const s = Math.sin(v.heading);
        for (const sphere of spec.hitSpheres) {
          const local = tentacleCentrePoint(pose, sphere.t, time);
          const centre = {
            x: v.pos.x + local.x * c + local.z * s,
            y: v.pos.y + local.y,
            z: v.pos.z - local.x * s + local.z * c,
          };
          const ca = closestApproach(from, step, centre, 1);
          if (ca.dist <= sphere.r && ca.t < strikeT) {
            strikeT = ca.t;
            struck = v;
            struckEye = false;
          }
        }
        continue;
      }

      // A capsule around the hull centreline is a good enough proxy for an OBB here and
      // is far cheaper: hulls are long and thin, which is exactly what a capsule is.
      const radius = Math.max(spec.beam, spec.height) * 0.5;
      const half = spec.length * 0.5;
      const fx = Math.sin(v.heading);
      const fz = Math.cos(v.heading);

      // Test against three points along the centreline — bow, midships, stern.
      for (let s = -1; s <= 1; s++) {
        const cx = v.pos.x + fx * half * s * 0.66;
        const cz = v.pos.z + fz * half * s * 0.66;
        const cy = v.pos.y + spec.height * 0.35;
        const ca = closestApproach(from, step, { x: cx, y: cy, z: cz }, 1);
        if (ca.dist <= radius && ca.t < strikeT) {
          strikeT = ca.t;
          struck = v;
          struckEye = false;
        }
      }
    }

    if (struck) {
      const point: Vec3 = {
        x: from.x + step.x * strikeT,
        y: from.y + step.y * strikeT,
        z: from.z + step.z * strikeT,
      };
      const speed = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
      hits.push({ projectile: p, vessel: struck, point, speed, ricochet: false, eye: struckEye });
      pool.release(p);
      continue;
    }

    // --- Water surface ----------------------------------------------------------------
    const surface = waterHeightAt(waves, p.pos.x, p.pos.z, time);

    if (p.kind === 'depthCharge') {
      if (p.phase === 'air' && p.pos.y <= surface) {
        // Measured before the horizontal velocity is killed, or the splash would scale
        // off the sink rate rather than off the speed the charge actually arrived at.
        const entrySpeed = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
        p.phase = 'sinking';
        p.pos.y = surface;
        p.vel.x = 0; p.vel.z = 0;
        /*
         * Water entry, reported the way the torpedo's is.
         *
         * It used to change phase in silence, so the charge crossed the surface with no
         * event at all and the only thing the presentation layer ever heard from this
         * weapon was the detonation at fuse depth. The splash the player watches — the
         * moment that says the lob was good — had nothing to hang on.
         *
         * The projectile stays active, which is what marks this hit as a pass-through
         * rather than the terminal one: the caller keys the burst off release.
         */
        hits.push({
          projectile: p, vessel: null, point: { x: p.pos.x, y: surface, z: p.pos.z },
          speed: entrySpeed, ricochet: false,
        });
      }
      // Detonation is handled by the caller via fuse depth; here we only track depth.
      if (p.phase === 'sinking' && p.fuseDepth !== undefined
          && surface - p.pos.y >= p.fuseDepth) {
        hits.push({ projectile: p, vessel: null, point: { ...p.pos }, speed: 0, ricochet: false });
        pool.release(p);
      }
      continue;
    }

    if (p.kind === 'torpedo') {
      // Water entry: the tube hop ends and the dive begins.
      if (p.phase === 'air' && p.pos.y <= surface) {
        p.phase = 'water';
        hits.push({
          projectile: p, vessel: null, point: { x: p.pos.x, y: surface, z: p.pos.z },
          speed: Math.hypot(p.vel.x, p.vel.y, p.vel.z), ricochet: false,
        });
      }

      /*
       * Proximity fuse, live only past the arming distance.
       *
       * Armed by distance run rather than by time, because that is the safety interlock a
       * real fish has and because it gives the player a genuine minimum range: a target
       * inside 100 m cannot be torpedoed, and between 100 m and the guaranteed range the
       * lock's hit-curve makes closeness itself the risk (TORPEDO_LOCK.hitCurve) — letting
       * something get close is a mistake with consequences rather than an inconvenience.
       */
      const run = Math.hypot(p.pos.x - p.originX, p.pos.z - p.originZ);
      if (run >= TORPEDO.armDistance) {
        let nearest: Vessel | null = null;
        let nearestDist = TORPEDO.fuseRadius;
        for (const v of vessels) {
          if (v.state === 'dead' || v.state === 'departing') continue;
          if (v.id === p.ownerId) continue;
          if (p.hostile && v.faction === 'hostile') continue;
          // A rolled miss cannot detonate against the hull it is fated to miss — the
          // run to the astern ghost point may pass within fuse range on the way. Any
          // OTHER hull in the way is still fair game.
          if (p.rigged === 'miss' && v.id === p.targetId) continue;
          const spec = vesselSpec(v.kind);
          // Measure to the hull, not its centre: a 180 m tanker is a wall, not a point.
          const fx = Math.sin(v.heading);
          const fz = Math.cos(v.heading);
          const rel = (p.pos.x - v.pos.x) * fx + (p.pos.z - v.pos.z) * fz;
          const along = Math.max(-spec.length * 0.5, Math.min(spec.length * 0.5, rel));
          const hx = v.pos.x + fx * along;
          const hz = v.pos.z + fz * along;
          const d = Math.hypot(p.pos.x - hx, p.pos.z - hz) - spec.beam * 0.5;
          if (d < nearestDist) { nearestDist = d; nearest = v; }
        }
        if (nearest) {
          hits.push({
            projectile: p, vessel: nearest, point: { ...p.pos },
            speed: Math.hypot(p.vel.x, p.vel.z), ricochet: false,
          });
          pool.release(p);
          continue;
        }
      }

      // Fuel exhausted: it sinks inert rather than vanishing.
      if (run > TORPEDO.maxRun) pool.release(p);
      continue;
    }

    if (p.pos.y <= surface) {
      const speed = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
      const horizontal = Math.hypot(p.vel.x, p.vel.z);
      const grazeAngle = Math.atan2(Math.abs(p.vel.y), Math.max(horizontal, 1e-6));
      /*
       * A shallow round may skip off the water like a stone: visible, and it keeps a long
       * miss interesting instead of simply vanishing. It is a chance, not a certainty,
       * and strictly limited — see AUTOCANNON.ricochetChance for what went wrong when it
       * was neither.
       */
      const canSkip = p.kind === 'shell'
        && grazeAngle < AUTOCANNON.ricochetGrazeAngle
        && p.skips < AUTOCANNON.ricochetMaxSkips
        && p.age < 12
        && rng.next() < AUTOCANNON.ricochetChance;
      const point: Vec3 = { x: p.pos.x, y: surface, z: p.pos.z };

      if (canSkip) {
        p.skips++;
        p.pos.y = surface + 0.4;
        // Bleed hard, and take more out of the horizontal than the vertical so the next
        // impact is *steeper* than this one. The old ratio did the reverse and made every
        // skip beget another.
        p.vel.y = Math.abs(p.vel.y) * 0.55;
        p.vel.x *= 0.45;
        p.vel.z *= 0.45;
        hits.push({ projectile: p, vessel: null, point, speed, ricochet: true });
      } else {
        hits.push({ projectile: p, vessel: null, point, speed, ricochet: false });
        pool.release(p);
      }
      continue;
    }

    // --- Expiry ------------------------------------------------------------------------
    const range = Math.hypot(p.pos.x, p.pos.z);
    if (range > AUTOCANNON.maxRange || p.age > 30 || p.pos.y > 6000) {
      pool.release(p);
    }
  }

  return hits;
}

/**
 * Gravity plus quadratic drag — the shape of the arc the player learns to read.
 *
 * Rockets fly dragless: they are powered for most of the arc, and — more to the point —
 * hostile aim in `hostileFire` is solved in vacuum. With the 30 mm's drag constant a
 * corvette volley from her 2.8 km hold fell ~200 m short of the platform every single
 * time, which is how her bombardment (and the FAC's long volleys) stayed cosmetic.
 */
function integrateBallistic(p: Projectile, dt: number): void {
  const speed = Math.hypot(p.vel.x, p.vel.y, p.vel.z);
  const drag = p.kind === 'rocket' ? 0 : AUTOCANNON.dragK * speed;
  p.vel.x -= p.vel.x * drag * dt;
  p.vel.y -= (p.vel.y * drag + GRAVITY) * dt;
  p.vel.z -= p.vel.z * drag * dt;
  p.pos.x += p.vel.x * dt;
  p.pos.y += p.vel.y * dt;
  p.pos.z += p.vel.z * dt;
}

/**
 * The torpedo's run (plan D2).
 *
 * The acceleration phase is the whole point of the weapon and the reason it is not simply
 * a slow shell: it leaves the tube barely making way, settles to running depth, and then
 * spends twelve seconds building from 5 to 30 m/s. That is a long time to watch something
 * you cannot recall, which is the feeling the design is buying. The gyro turn rate is
 * deliberately far too slow to chase anything — it corrects a launch bearing, it does not
 * hunt — so the player's aim at launch is what decides the outcome.
 */
function integrateTorpedo(
  p: Projectile, vessels: readonly Vessel[], dt: number, speedScale = 1,
): void {
  if (p.phase === 'air') {
    // Tube launch: a short ballistic hop before water entry.
    p.vel.y -= GRAVITY * dt;
  } else if (p.phase === 'water') {
    /*
     * Dive to running depth. Modelled as an approach rather than a snap so the entry
     * reads as a body settling into the water, and so the wake feather has a moment of
     * being deep and invisible before it shallows out.
     */
    const depthError = (-TORPEDO.runDepth) - p.pos.y;
    p.vel.y = Math.max(-4, Math.min(4, depthError * 1.6));
    if (Math.abs(depthError) < 0.35) {
      p.phase = 'running';
      p.pos.y = -TORPEDO.runDepth;
      p.vel.y = 0;
    }
    steerAndAccelerate(p, vessels, dt, speedScale);
  } else {
    p.vel.y = 0;
    p.pos.y = -TORPEDO.runDepth;
    steerAndAccelerate(p, vessels, dt, speedScale);
  }
  p.pos.x += p.vel.x * dt;
  p.pos.y += p.vel.y * dt;
  p.pos.z += p.vel.z * dt;
}

/**
 * Gyro correction toward the soft-locked target, then the acceleration curve.
 *
 * A rigged fish (TORPEDO_LOCK — outcome rolled at launch) steers with far more
 * authority than the honest gyro: enough to always reach a doomed hull, or to track
 * the point just astern that a rolled miss falls into. Unrolled fish — divers, the
 * boss, anything fired before a target died — keep the modest physical turn rate.
 */
function steerAndAccelerate(
  p: Projectile, vessels: readonly Vessel[], dt: number, speedScale = 1,
): void {
  const target = vessels.find((v) => v.id === p.targetId);
  const speed = Math.hypot(p.vel.x, p.vel.z);
  let heading = Math.atan2(p.vel.x, p.vel.z);

  if (target && target.state !== 'dead') {
    let wantX = target.pos.x;
    let wantZ = target.pos.z;
    if (p.rigged === 'miss') {
      // The ghost point astern of the hull. Each fish gets its own extra metres from
      // its id, so repeated misses do not all thread the identical line.
      const spec = vesselSpec(target.kind);
      const astern = spec.length * 0.5 + TORPEDO_LOCK.missOffset
        + (Math.sin(p.id * 78.233) * 0.5 + 0.5) * 12;
      wantX -= Math.sin(target.heading) * astern;
      wantZ -= Math.cos(target.heading) * astern;
      // Once it reaches the ghost point it is past its moment: release the target and
      // run straight, or a missed fish would trail the stern until its fuel ran out.
      if (Math.hypot(wantX - p.pos.x, wantZ - p.pos.z) < 30) {
        p.targetId = null;
        p.rigged = null;
      }
    }
    const want = Math.atan2(wantX - p.pos.x, wantZ - p.pos.z);
    let delta = (want - heading) % (Math.PI * 2);
    if (delta > Math.PI) delta -= Math.PI * 2;
    if (delta < -Math.PI) delta += Math.PI * 2;
    const rate = p.rigged !== null ? TORPEDO_LOCK.riggedTurnRate : TORPEDO.turnRate;
    const maxTurn = rate * dt;
    heading += Math.max(-maxTurn, Math.min(maxTurn, delta));
  }

  /*
   * Gyro drift: its own slow curve, on top of whatever correction it just made.
   *
   * Derived from the projectile id rather than drawn from the Rng, so it is stable for
   * the whole run — a bias that resampled every tick would be a wobble, and what this
   * models is a weapon that is very slightly, consistently, off. Deterministic either
   * way, but only this version actually curves.
   */
  heading += Math.sin(p.id * 12.9898) * TORPEDO.gyroDrift * dt;

  /*
   * The screws scale the whole curve — start, ceiling and the acceleration between —
   * so the weapon keeps its shape and simply covers ground faster. Scaling only the
   * ceiling would have made it accelerate for longer instead of moving quicker, which
   * is not what "+25% speed" means to anyone reading it.
   */
  const startSpeed = TORPEDO.speedStart * speedScale;
  const maxSpeed = TORPEDO.speedMax * speedScale;
  const accel = (maxSpeed - startSpeed) / TORPEDO.accelSeconds;
  const next = Math.min(maxSpeed, Math.max(speed, startSpeed) + accel * dt);
  p.vel.x = Math.sin(heading) * next;
  p.vel.z = Math.cos(heading) * next;
}

/**
 * The depth charge's lob and sink (plan D3).
 *
 * It tumbles through the air — the renderer reads `age` for the rotation — then loses
 * essentially all horizontal speed at entry and sinks at a constant rate. Constant is
 * correct here rather than lazy: a charge reaches terminal velocity almost immediately,
 * and a predictable sink rate is what lets the player time a fuse depth against a
 * submarine's track, which is the entire skill of the weapon.
 */
function integrateDepthCharge(p: Projectile, dt: number): void {
  if (p.phase === 'air') {
    p.vel.y -= GRAVITY * dt;
  } else {
    p.vel.y = -DEPTH_CHARGE.sinkRate;
    // A little residual drift, so a charge does not sink on a perfect plumb line.
    p.vel.x *= 0.92;
    p.vel.z *= 0.92;
  }
  p.pos.x += p.vel.x * dt;
  p.pos.y += p.vel.y * dt;
  p.pos.z += p.vel.z * dt;
}

/**
 * Spherical damage from an underwater burst, computed in 3D so depth genuinely matters.
 *
 * This is what makes the fuse selector a decision rather than a formality: a shallow
 * fuse chips surface craft and misses a deep sub entirely, and a deep fuse is the
 * sub-killer that surface vessels ride out.
 */
export function detonationDamage(
  centre: Vec3,
  vessels: readonly Vessel[],
  lethalRadius: number,
  falloffRadius: number,
  damage: number,
): Array<{ vessel: Vessel; amount: number }> {
  const out: Array<{ vessel: Vessel; amount: number }> = [];
  for (const v of vessels) {
    if (v.state === 'dead' || v.state === 'departing') continue;
    const spec = vesselSpec(v.kind);
    // Measure to the hull's centre of volume, not its waterline origin: a submerged
    // boat's mass is below the surface and that is what the shockwave has to reach.
    // A submerged hull is where its own depth says it is, not just under the waterline.
    /*
     * `height * 0.3` is the centre of volume of a ship — a box standing on the water —
     * and it is nonsense for the creature, whose 310 m of "height" is a mantle lying IN
     * the sea with a crown above it. It put the Kraken's blast centre 93 m in the air, so
     * a depth charge going off twenty-five metres beneath a surfaced animal missed it by
     * more than a hundred metres and the weapon did nothing at all against the phase it
     * was most needed in. Its mass is at the waterline; that is where a burst reaches it.
     */
    const cy = v.pos.y
      + (v.submerged
        ? -Math.max(spec.draft * 0.5, v.submergedDepth)
        : v.kind === 'kraken' ? 4 : spec.height * 0.3);
    const d = Math.hypot(v.pos.x - centre.x, cy - centre.y, v.pos.z - centre.z);
    if (d >= falloffRadius) continue;
    const t = d <= lethalRadius
      ? 1
      : 1 - (d - lethalRadius) / Math.max(falloffRadius - lethalRadius, 1e-6);
    out.push({ vessel: v, amount: damage * t });
  }
  return out;
}

/**
 * Solve the launch elevation that puts a drag-affected round on a target at `range`.
 *
 * There is no closed form once quadratic drag is in play, so this shoots a short
 * numerical simulation and bisects on elevation. It runs when the player selects a target
 * (not per frame), and it is what makes the scope's lead pip honest rather than decorative.
 */
export function solveElevation(range: number, heightDrop: number): number {
  /*
   * The bracket has to cover steep depression as well as long-range elevation. The old
   * lower bound of -0.3 rad (about 17 degrees down) could not express a shot at anything
   * close to the platform, so near targets silently got a bad solution rather than none.
   */
  let lo = -0.9;
  let hi = 0.6;
  for (let iter = 0; iter < 24; iter++) {
    const mid = (lo + hi) * 0.5;
    const drop = simulateDrop(range, mid);
    if (drop === null) { hi = mid; continue; }
    if (drop > heightDrop) hi = mid; else lo = mid;
  }
  return (lo + hi) * 0.5;
}

/** Height of the round relative to the muzzle when it reaches `range` horizontally. */
function simulateDrop(range: number, elevation: number): number | null {
  const dt = 1 / 60;
  let x = 0;
  let y = 0;
  let vx = AUTOCANNON.muzzleVelocity * Math.cos(elevation);
  let vy = AUTOCANNON.muzzleVelocity * Math.sin(elevation);
  for (let i = 0; i < 60 * 30; i++) {
    const speed = Math.hypot(vx, vy);
    const drag = AUTOCANNON.dragK * speed;
    vx -= vx * drag * dt;
    vy -= (vy * drag + GRAVITY) * dt;
    const prevX = x;
    const prevY = y;
    x += vx * dt;
    y += vy * dt;
    if (x >= range) {
      const t = (range - prevX) / Math.max(x - prevX, 1e-9);
      return prevY + (y - prevY) * t;
    }
    if (vx <= 1) return null;
  }
  return null;
}

/** Time of flight to a horizontal range, for the lead computation and for tests. */
export function timeOfFlight(range: number, elevation: number): number {
  const dt = 1 / 60;
  let x = 0;
  let vx = AUTOCANNON.muzzleVelocity * Math.cos(elevation);
  let vy = AUTOCANNON.muzzleVelocity * Math.sin(elevation);
  for (let i = 0; i < 60 * 30; i++) {
    const speed = Math.hypot(vx, vy);
    const drag = AUTOCANNON.dragK * speed;
    vx -= vx * drag * dt;
    vy -= (vy * drag + GRAVITY) * dt;
    x += vx * dt;
    if (x >= range) return i * dt;
    if (vx <= 1) return Infinity;
  }
  return Infinity;
}
